import React, { useEffect, useState } from "react";
import "./Forecast.css";
import L from 'leaflet';
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { MapContainer, TileLayer, Marker, Tooltip, LayersControl, GeoJSON } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { MapHomeControl, MapInfoControl } from "../../../common/MapControls/MapControls";

const HydroForecast = () => {
    const backendUrl = process.env.REACT_APP_BACKEND_API_URL_LOCAL;
    const URLTILEMAP_WHITE = "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png";

    const [boundaryData, setBoundaryData] = useState(null);
    const [rnTl, setrnTl] = useState(null);
    const [selectedRiver, setSelectedRiver] = useState(null);
    const [stations, setStations] = useState([]);
    const [chartOptions, setChartOptions] = useState(null);
    const [loading, setLoading] = useState(false);
    const [showChartModal, setShowChartModal] = useState(false);

    const [forecastDate, setForecastDate] = useState(() => {
        const yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 1);
        const yyyy = yesterday.getFullYear();
        const mm = String(yesterday.getMonth() + 1).padStart(2, '0');
        const dd = String(yesterday.getDate()).padStart(2, '0');
        return `${yyyy}-${mm}-${dd}`;
    });

    const stationIcon = L.icon({
        iconUrl: "/DATA/Flood/outlet.png", // replace filename as needed
        iconSize: [15, 15],
        iconAnchor: [7, 7]
    });

    // Fetch stations
    useEffect(() => {
        fetch(`${backendUrl}/flood/geoglows-stations/`)
            .then(res => res.json())
            .then(data => setStations(data.filter(s => s.geoglow_status === 1)))
            .catch(err => console.error("Error loading stations:", err));
    }, [backendUrl]);

    // Load boundary & river network
    useEffect(() => {
        fetch("/DATA/OSMbased_adm1_boundary.json").then(res => res.json()).then(setBoundaryData);
        fetch("/DATA/Flood/tl_rn_final.geojson").then(res => res.json()).then(setrnTl);
    }, []);

    // Fetch forecast data and prepare Highcharts options
    useEffect(() => {
        if (!selectedRiver || !forecastDate) return;
        setLoading(true);

        const dateFormatted = forecastDate.replace(/-/g, "");
        fetch(`https://geoglows.ecmwf.int/api/v2/forecast/${selectedRiver}?format=json&date=${dateFormatted}`)
            .then(res => res.json())
            .then(data => {
                const { datetime, flow_median, flow_uncertainty_lower, flow_uncertainty_upper } = data;
                const utcTimes = datetime.map(dt => Date.parse(dt)); // keep exact UTC

                const options = {
                    chart: { zoomType: "x", backgroundColor: "#f9f9f9" },
                    //title: { text: `Forecast for ${stations.find(s => s.river_id === selectedRiver)?.station_name || 'Unknown'}`, style: { fontSize: '18px' } },
                    title: { text: null },
                    xAxis: { type: "datetime", title: { text: "Date (UTC)" }, labels: { format: "{value:%d %b %H:%M}" } },
                    yAxis: { title: { text: "Discharge (m³/s)" }, min: 0 },
                    tooltip: { shared: true, xDateFormat: "%d %b %Y %H:%M UTC", valueDecimals: 2, valueSuffix: " m³/s" },
                    legend: { enabled: true },
                    series: [
                        { name: "Upper Flow", type: "line", data: utcTimes.map((t, i) => [t, flow_uncertainty_upper[i]]), color: "#cc3300", dashStyle: "Dash", lineWidth: 1.5 },
                        { name: "Median Flow", type: "line", data: utcTimes.map((t, i) => [t, flow_median[i]]), color: "#000000", lineWidth: 2 },
                        { name: "Lower Flow", type: "line", data: utcTimes.map((t, i) => [t, flow_uncertainty_lower[i]]), color: "#0077cc", dashStyle: "Dash", lineWidth: 1.5 },

                    ],
                    credits: { enabled: false }
                };

                setChartOptions(options);
            })
            .catch(err => {
                console.error("Error fetching data:", err);
                setChartOptions(null);
            })
            .finally(() => setLoading(false));
    }, [selectedRiver, forecastDate, stations]);

    return (
        <div className="map-container">
            {/* Chart Modal */}
            {showChartModal && chartOptions && (
                <div className="chart-modal">
                    <div className="chart-modal-header">
                        <h3>
                            Forecast for {stations.find(s => s.river_id === selectedRiver)?.station_name || "Unknown"}
                        </h3>
                        <button className="chart-modal-close" onClick={() => setShowChartModal(false)}> × </button>
                    </div>
                    <div className="chart-modal-content">
                        {loading ? <div className="forecast-loader"><img src="/loader.webp" alt="Loading..." /></div>
                            : <HighchartsReact highcharts={Highcharts} options={chartOptions} />}
                    </div>
                </div>
            )}

            {/* Date selector */}
            <div style={{ position: "absolute", top: 20, left: 50, zIndex: 500, background: "rgba(255, 255, 255, 0.6)", padding: 5, borderRadius: 8, boxShadow: "0 2px 10px rgba(0,0,0,0.1)" }}>
                <div style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: 8, fontFamily: "Helvetica" }}>
                    <label htmlFor="forecast-date" style={{ fontSize: 14, fontWeight: 500 }}>Select Date : </label>
                    <input
                        type="date"
                        id="forecast-date"
                        value={forecastDate}
                        onChange={e => setForecastDate(e.target.value)}
                        style={{ padding: 8, borderRadius: 4, border: "1px solid #ccc", fontSize: 14, fontFamily: "Helvetica", minWidth: 150 }}
                    />
                </div>
            </div>

            {/* Map */}
            <MapContainer center={[-8.7950, 125.6877]} zoom={9} zoomControl={false} attributionControl={false} style={{ height: 550, width: "100%", borderRadius: 12 }}>
              <MapHomeControl />
              <MapInfoControl text="GEOGloWS streamflow forecast. Click a river reach to view the forecast hydrograph." />
                <LayersControl position="topright">
                    <LayersControl.Overlay name="Timor-Leste">
                        {boundaryData && <GeoJSON data={boundaryData} interactive={false} style={{ color: "#ff0000", weight: 1, fillOpacity: 0 }} />}
                    </LayersControl.Overlay>
                    <LayersControl.Overlay name="River Networks" checked>
                        {rnTl && <GeoJSON data={rnTl} interactive={false} style={{ color: "#0000ff", weight: 1, fillOpacity: 0 }} />}
                    </LayersControl.Overlay>
                </LayersControl>
                <TileLayer url={URLTILEMAP_WHITE} />
                {stations.map(station => (
                    <Marker key={station.id} icon={stationIcon} position={[station.latitude, station.longitude]} eventHandlers={{ click: () => { setSelectedRiver(station.river_id); setShowChartModal(true); setLoading(true); } }}>
                        <Tooltip direction="right" offset={[8, 0]} opacity={1} permanent={false}>{station.station_name}</Tooltip>
                    </Marker>
                ))}
            </MapContainer>
        </div>
    );
};

export default HydroForecast;
